/* reco-log.js - writes down what the recommendations did to this page.
 *
 * Dorian 2026-11-04: "a nice log that shows the event: the user did this -> then
 * this element was replaced there", and "initial state from table + live actions".
 * Isaiah's Storytelling > Website changes reads these rows. What a visitor did was
 * already recorded (page_view, card_click, add_to_cart); what the website did in
 * answer was not: recommendations.js reorders the page and leaves no trace.
 *
 * WHAT IT SENDS: one `reco_effect` for each thing on the page a list governs.
 *   surface    home_products | home_collections | header_links
 *   cause      table (the list saved from the tables) | session (re-ranked from this visit)
 *   src        where a saved list came from: table (BI) | legacy (the old engine)
 *   moved_up   how many moved up. One card brought forward shifts every card it
 *              passes, and those did nothing but make room, so they are not counted.
 *   total      how many items the surface has
 *   moves      [{h, was, now}], at most 12
 * Everything rides in `detail`. No key here may be called product, variant or
 * collection: the ingest reads those as objects and loses the event for a string.
 *
 * MEASURED, NOT ASSUMED: the order is read off the page before and after each apply,
 * never taken from what the code was asked to do. The selectors are the ones
 * recommendations.js uses. agb:recs-applied fires at the START of its update and the
 * reorder runs in that same call, so a timeout lands after it.
 *
 * Reads the page and storage, writes nothing to either but one tab-scoped flag. Goes
 * out through CAEDev, so a test customer's rows are tagged like all their others and
 * no report counts them. Nothing is sent while a visitor has no list to apply.
 * Must load BEFORE recommendations.js: a saved list is applied as soon as that file
 * runs. Own file, edits nothing upstream. Kill: window.AGB_RECO_LOG_OFF = true.
 */
(function () {
  'use strict';
  if (window.AGB_RECO_LOG_OFF || window.__agbRecoLog) return;
  window.__agbRecoLog = true;

  var SURFACES = [
    {
      name: 'home_products',
      root: '#featured-collections-product-list-carousel-template--17094519816444__ec64810e-9fe1-4330-9c9d-6b05ac253e2e-collection-1',
      item: 'product-card', attr: 'handle', switchKey: 'productReorder'
    },
    { name: 'home_collections', root: '.collection-list', item: '.collection-card', attr: 'href', switchKey: 'collectionReorder' }
  ];
  var MOVES_MOST = 12;
  var SEND_AFTER_MS = 800;       // after this page's page_view, so the log reads in order
  var HEADER_SENT_KEY = 'AGB_RecoLogHeader';

  function orderOf(s) {
    var root = document.querySelector(s.root);
    if (!root) return [];
    return Array.prototype.map.call(root.querySelectorAll(s.item), function (el) {
      return el.getAttribute(s.attr) || '';
    });
  }

  // What the visitor last saw, so each apply is measured against the one before it.
  var shown = {};
  SURFACES.forEach(function (s) { shown[s.name] = orderOf(s); });
  var told = {};

  function movedUp(was, now) {
    var from = {};
    was.forEach(function (id, i) { if (!(id in from)) from[id] = i + 1; });
    var out = [];
    now.forEach(function (id, i) {
      if (from[id] && i + 1 < from[id]) out.push({ h: id, was: from[id], now: i + 1 });
    });
    return out;
  }
  function storedCount() {
    try {
      var list = JSON.parse(localStorage.getItem('recommendedProducts') || '[]');
      return Array.isArray(list) ? list.length : 0;
    } catch (e) { return 0; }
  }
  function listSource() {
    try { return localStorage.getItem('recommendedSource') || 'unknown'; } catch (e) { return 'unknown'; }
  }
  function switchedOff(key) {
    try {
      var d = window.__AGB_dynamicUI;
      var f = d && (d.features || d);
      return !!f && f[key] === false;
    } catch (e) { return false; }
  }

  // cae-client.js loads after this file, and a saved list can land before it has.
  function send(detail, tries) {
    try {
      if (window.CAEDev && typeof window.CAEDev.emit === 'function') {
        window.CAEDev.emit('reco_effect', detail);
        return;
      }
    } catch (e) { return; }
    if ((tries || 0) > 40) return;
    setTimeout(function () { send(detail, (tries || 0) + 1); }, 200);
  }
  function sendLater(detail) {
    setTimeout(function () { send(detail, 0); }, SEND_AFTER_MS);
  }

  function recordHeader(cause, src) {
    var added = document.querySelectorAll('li[data-agb-nav-added]');
    if (!added.length || switchedOff('nav')) return;
    // The links go back in on every page, so once a tab says it, it has been said.
    try {
      if (sessionStorage.getItem(HEADER_SENT_KEY) === '1') return;
      sessionStorage.setItem(HEADER_SENT_KEY, '1');
    } catch (e) { return; }
    sendLater({
      surface: 'header_links',
      cause: cause,
      src: src,
      moved_up: added.length,
      total: added.length,
      links: Array.prototype.map.call(added, function (li) {
        return String(li.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 40);
      })
    });
  }

  function record(session) {
    var cause = session ? 'session' : 'table';
    var src = session ? null : listSource();
    SURFACES.forEach(function (s) {
      var now = orderOf(s);
      if (!now.length || switchedOff(s.switchKey)) return;
      var moved = movedUp(shown[s.name] || [], now);
      shown[s.name] = now;
      // One line per cause per page when nothing moved; every real move gets its own.
      var key = s.name + '|' + cause;
      if (!moved.length && told[key]) return;
      told[key] = true;
      sendLater({
        surface: s.name,
        cause: cause,
        src: src,
        moved_up: moved.length,
        total: now.length,
        moves: moved.slice(0, MOVES_MOST)
      });
    });
    recordHeader(cause, src);
  }

  document.addEventListener('agb:recs-applied', function (e) {
    var session = !!(e && e.detail && e.detail.session);
    setTimeout(function () {
      try {
        // A visitor with no saved list can still be re-ranked from what they do.
        if (session || storedCount()) record(session);
      } catch (err) {}
    }, 0);
  });
})();
